import * as React from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import AccessTime from '@mui/icons-material/AccessTime'
import EventAvailable from '@mui/icons-material/EventAvailable'
import AttachMoney from '@mui/icons-material/AttachMoney'
import PersonOutline from '@mui/icons-material/PersonOutline'
import HowToReg from '@mui/icons-material/HowToReg'
import { useTheme } from '@mui/material/styles'

type RowIcon = 'time' | 'due' | 'value' | 'contact' | 'owner'

const ICONS: Record<RowIcon, typeof AccessTime> = {
  time: AccessTime,
  due: EventAvailable,
  value: AttachMoney,
  contact: PersonOutline,
  owner: HowToReg,
}

type Props = {
  icon: RowIcon
  label: string
  value?: string
  /** Reserve the row with a skeleton while its value hydrates, so it fills in
   *  place rather than popping in later and shifting the rows below it. */
  loading?: boolean
}

/**
 * One label/value fact row in the detail popover. While `loading`, the row is
 * reserved with a skeleton value. Once loaded, a row with no value is removed
 * from the DOM (SH-1986 Q3): the body is a gap column, so a hidden-but-present
 * row would leave a stray gap — the row must leave the DOM entirely.
 */
export function DetailRow({ icon, label, value, loading }: Props) {
  const theme = useTheme()
  if (!loading && !value) return null

  const Icon = ICONS[icon]

  return (
    <Box sx={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <Icon
        aria-hidden
        sx={{
          fontSize: 17,
          color: theme.palette.text.secondary,
          mt: '2px',
          flex: 'none',
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '.05em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
          }}
        >
          {label}
        </Typography>
        {loading && !value ? (
          <Skeleton
            variant="rectangular"
            width={140}
            sx={{ height: '16px', borderRadius: '4px', mt: '3px' }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: '14px',
              mt: '1px',
              color: theme.palette.text.primary,
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
